"use client";

import { BadgeCheck, Bell, ChevronsUpDown, Gift, LifeBuoy, LogOut } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import React from "react";
import { useNavigate } from "react-router-dom";
import { getInitials } from "@/util/string";
import { cn } from "@/lib/utils";
import { HUBSPOT_PORTAL_ID, openSupportChat } from "@/components/provider/HubSpotChatLoader";
import { useBetaBannerDismissed } from "@/hooks/useBetaBanner";
import { useGetHomeQuery, useGetReferralsQuery } from "@/store/rest_api";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";

export function DashboardNavUser({
  user,
}: {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
}) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const betaBannerDismissed = useBetaBannerDismissed();
  const { data: home } = useGetHomeQuery();
  const membership = home?.activeMembership;
  const canManage =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);
  const { data: referrals } = useGetReferralsQuery(undefined, { skip: !canManage });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                !betaBannerDismissed && "ring-2 ring-inset ring-primary"
              )}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.firstName} />
                <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">{getInitials(user.firstName, user.lastName)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {user.firstName}&nbsp;{user.lastName}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.firstName} />
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">{getInitials(user.firstName, user.lastName)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user.firstName}&nbsp;{user.lastName}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate("/account")}>
                <BadgeCheck className="text-primary" />
                Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/notifications")}>
                <Bell className="text-primary" />
                Notifications
              </DropdownMenuItem>
              {referrals?.enabled && (
                <DropdownMenuItem onClick={() => navigate("/referrals")}>
                  <Gift className="text-primary" />
                  Refer and earn
                </DropdownMenuItem>
              )}
              {HUBSPOT_PORTAL_ID && (
                <DropdownMenuItem onClick={() => openSupportChat()}>
                  <LifeBuoy className="text-primary" />
                  Support
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <a href="/logout">
              <DropdownMenuItem>
                <LogOut className="text-destructive" />
                Log out
              </DropdownMenuItem>
            </a>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
